// Client entry: the boot sequence (§23.1) and the main loop (§23.2). The frame source is the
// offline LocalSession in the menu and Practice, the online ClientGame while in a room, a replay
// wrapping it (Instant Replay, BONK CAM, Play of the Match), the stage of a `?debug=`
// verification page, or the `?debug=ui` gallery's fake room.
import { NO_SLOT } from '@shared/constants';
import { EV } from '@shared/events';
import { Difficulty, MapId, ModeId, RoomPhase } from '@shared/ids';
import { cleanName } from '@shared/names';
import type { ClientMsg, RoomSettings, RoomStateDTO } from '@shared/protocol/messages';
import { hashWorld } from '@shared/sim/world';
import { createAudioEngine, type AudioEngine } from './audio/AudioEngine';
import { detectQuality, hasWebGL2, loadFonts, showFallback, wsUrl } from './boot';
import { createBus, onSim, type Bus } from './core/bus';
import { readParams, roomRequestFromParams, type DebugPage, type Params } from './core/params';
import type { StageRound } from './core/testStage';
import { now } from './core/time';
import { installDebug, type VerificationPage } from './debug';
import { ClientGame } from './game/ClientGame';
import { Excitement } from './game/Excitement';
import { createFrameView, type FrameSource, type FrameView } from './game/FrameView';
import { LocalSession, MENU_SESSION, type LocalSessionConfig } from './game/LocalSession';
import { Tapestry } from './game/Tapestry';
import { createAimContext, syncAimContext } from './input/aimAssist';
import { createInputManager } from './input/InputManager';
import { Net, type SessionStore } from './net/Net';
import { loadProfile } from './profile/Profile';
import { createProfileService } from './profile/ProfileService';
import { createFirstPrompts } from './profile/prompts';
import { createSettingsStore, type Settings } from './profile/settings';
import { clearReconnect, loadReconnect, saveReconnect } from './profile/storage';
import { ReplayPlayer } from './render/replay/ReplayPlayer';
import { createRenderer } from './render/Renderer';
import type { Gallery } from './ui/debug/gallery';
import type { PracticeSetup, UiServices } from './ui/deps';
import { ERROR_TOASTS, JOIN_ERROR_TEXT, KICK_TOASTS } from './ui/labels';
import { PortraitCache, sameCosmetics } from './ui/portraits';
import { RoomState, ROOM_PHASE_NAMES } from './ui/RoomState';
import type { ConnectionState } from './ui/screens/Connection';
import { createShell, GAMEPLAY_OFF_SCREENS } from './ui/Shell';

/** Room phases whose world is stepped and snapshotted (§21.5): the client waits for a snapshot. */
const STEPPING_PHASES: readonly RoomPhase[] = [RoomPhase.LOBBY, RoomPhase.INTRO, RoomPhase.PLAY, RoomPhase.ROUND_END, RoomPhase.PODIUM];
/** Reconnect attempts before the overlay offers "Back to menu" (≈ the 30 s slot hold, §9.6). */
const RECONNECT_GIVE_UP = 6;
/** Failed attempts of a first connection (no room yet) before offering "Back to menu". */
const CONNECT_GIVE_UP = 3;
const SCREENSHOT_MIN_FRAMES = 30;
/** The `?debug=ui` gallery shows "Reconnecting… (3)" as in §14.2. */
const GALLERY_RECONNECT_ATTEMPT = 3;
/** §11: BONK CAM starts 0.3 s after the local bean's KO. */
const BONK_CAM_DELAY_MS = 300;

const sessionStore: SessionStore = { load: loadReconnect, save: saveReconnect, clear: clearReconnect };

function requireElement<T extends HTMLElement>(id: string, type: new () => T): T {
  const node = document.getElementById(id);
  if (!(node instanceof type)) throw new Error(`#${id} missing from index.html`);
  return node;
}

type VerificationPageId = Exclude<DebugPage, 'stats' | 'solids'>;

function verificationPageId(page: DebugPage | null): VerificationPageId | null {
  return page === 'cosmetics' || page === 'vfx' || page === 'audio' || page === 'maps' ? page : null;
}

/**
 * Builds a `?debug=` verification page; stages report their rounds through `onRound`. Loaded on
 * demand: players never download the verification pages (separate chunks).
 */
async function mountVerificationPage(id: VerificationPageId, root: HTMLElement, bus: Bus, audio: AudioEngine, onRound: (r: StageRound) => void): Promise<VerificationPage> {
  const { TestStage } = await import('./core/testStage');
  switch (id) {
    case 'cosmetics': return (await import('./render/bean/CosmeticsPage')).createCosmeticsPage(root, audio);
    case 'vfx': return (await import('./render/vfx/VfxPage')).createVfxPage(root, bus, new TestStage(onRound));
    case 'audio': return (await import('./audio/Soundboard')).createSoundboard(root, { bus, audio, stage: new TestStage(onRound) });
    case 'maps': return (await import('./render/maps/debug/MapsPage')).createMapsPage(root, bus, new TestStage(onRound));
  }
}

/** `?debug=ui` (WP9b's screen gallery) is not one of the core `DebugPage` values. */
function uiGalleryRequested(): boolean {
  return new URLSearchParams(location.search).get('debug') === 'ui';
}

function practiceConfig(s: PracticeSetup): LocalSessionConfig {
  return { mapId: s.mapId, modeId: s.modeId, bots: s.bots, difficulty: s.difficulty, practice: true, dummies: s.dummies };
}

async function boot(): Promise<void> {
  // 1–2. Params, then the WebGL2 check.
  const params: Params = readParams();
  if (!hasWebGL2()) {
    showFallback();
    return;
  }
  // 3. Fonts. 4. Settings and profile (each read falls back to defaults).
  const fontsLoaded = await loadFonts();
  const galleryMode = uiGalleryRequested();
  // The gallery's demo profile and settings never touch the player's real save.
  // The `?debug=ui` screen gallery is its own chunk, loaded only when asked for.
  const galleryModule = galleryMode ? await import('./ui/debug/gallery') : null;
  if (galleryModule) galleryModule.isolateGalleryStorage();
  const bus = createBus();
  const settingsStore = createSettingsStore(bus);
  let settings: Settings = settingsStore.get();
  const initial = loadProfile();
  if (params.name !== null) initial.name = cleanName(params.name) ?? initial.name;
  const profiles = createProfileService({ bus, initial });

  // 5. Bus, renderer, input, audio, UI and net.
  const canvas = requireElement('game', HTMLCanvasElement);
  const uiRoot = requireElement('ui', HTMLDivElement);
  const renderer = createRenderer({
    bus, tier: detectQuality(params, settings), forcedQuality: params.quality !== null, nopost: params.nopost, skipIntro: params.skipIntro,
    solidsOverlay: params.debug === 'solids',
  });
  await renderer.init(canvas, settings);

  let muted = params.mute;
  const audio = createAudioEngine({ bus, project: (x, y, z, out) => renderer.worldToScreen(x, y, z, out) });
  const applyVolumes = (): void => audio.setVolumes({ ...settings.audio, master: muted ? 0 : settings.audio.master });
  applyVolumes();
  const unlockAudio = (): void => {
    if (audio.unlocked) return;
    void audio.unlock().then(() => bus.emit('audio:unlocked', undefined));
  };

  // Chrome logs a console error for navigator.vibrate before the page's first tap ("Blocked call
  // to navigator.vibrate…"); touch haptics simply wait for a user gesture (§14.6).
  const vibrate = typeof navigator.vibrate === 'function' ? navigator.vibrate.bind(navigator) : null;
  if (vibrate) {
    Object.defineProperty(navigator, 'vibrate', {
      configurable: true,
      value: (pattern: VibratePattern) => (navigator.userActivation && !navigator.userActivation.hasBeenActive ? false : vibrate(pattern)),
    });
  }
  const input = createInputManager({
    bus, settings, onMute: () => {
      muted = !muted;
      applyVolumes();
      bus.emit('toast', { text: muted ? 'Sound off' : 'Sound on', kind: 'info' });
    },
  });
  input.attach(canvas, uiRoot);
  const aim = createAimContext((sx, sy, out) => renderer.groundFromScreen(sx, sy, out));

  // Reconnect attempts wait while the browser is offline (no failed sockets, no console errors).
  const net = new Net({ url: wsUrl(), bus, session: sessionStore, lagMs: params.lag, now, isOnline: () => navigator.onLine, hello: () => {
    const p = profiles.get();
    return { name: p.name, profileId: p.profileId, cosmetics: p.cosmetics };
  } });
  const game = new ClientGame({ net, bus, input });
  game.setGroundFromScreen((sx, sy, out) => renderer.groundFromScreen(sx, sy, out));
  if (params.autoplay) game.setAutoplay(true);

  const local = new LocalSession({
    bus, input, aim, profile: () => profiles.get(), autoplay: params.autoplay,
    onRound: (info) => {
      renderer.setRound(info);
      audio.setRound({ ...info, roundIndex: 0, startRoomTick: 0 });
    },
  });
  const room = new RoomState(bus, now);
  const excitement = new Excitement(bus);
  const replays = new ReplayPlayer(bus);
  const tapestry = new Tapestry(bus);
  const prompts = createFirstPrompts({ bus, profiles, glyph: (a) => input.glyph(a) });
  const portraits = new PortraitCache((c, id, size) => renderer.renderPortrait(c, id, size));
  const gallery: Gallery | null = galleryModule ? galleryModule.createGallery({ bus, renderer, input, aim, profiles, tapestry }) : null;

  // ---- Session control ------------------------------------------------------------------------
  let source: 'local' | 'online' = 'local';
  let practiceSetup: PracticeSetup | null = null;
  let pendingJoin = false;
  /** `?room=CODE`: create the room with that code if it does not exist (§23.1 step 7). */
  let createIfMissing = params.room !== null;
  let reconnectAttempt = 0;
  let lastView: FrameView = createFrameView();
  let presented: FrameView = lastView;
  /** The frame the renderer actually drew (hitstop holds, slow motion, actors lifted onto tilted floors). */
  let drawn: FrameView = lastView;
  /** When the pending BONK CAM starts (ms, the frame clock), or -1. */
  let bonkCamAt = -1;
  /** The local ghost released control to watch others (§9.6). */
  let ghostSpectating = false;
  /** The local player pressed skip on the current shared replay. */
  let skipVoted = false;
  let lastPhase: RoomPhase | null = null;
  /** Who credited each KO (ghost spectating follows the bean that KO'd you, §9.6). */
  const lastKiller = new Map<number, number>();
  let lastPhaseEnd = 0;
  /** A deep link waits for the name prompt (§9.2: `/r/CODE` after the name prompt on first visit). */
  let proceeded = false;
  /** The renderer draws the Locker turntable instead of the world (it does not count those frames). */
  let lockerOpen = false;
  let lockerFrames = 0;

  const transport = {
    connect(): void {
      if (gallery) return;
      net.connect();
    },
    send(msg: ClientMsg): void {
      if (gallery) gallery.server.send(msg);
      else net.send(msg);
    },
  };

  function stopReplay(): void {
    bonkCamAt = -1;
    skipVoted = false;
    replays.skip();
  }

  /** The live source a replay covers (online game, or the gallery stage). */
  function liveSource(): FrameSource {
    return gallery?.source ?? game;
  }

  /** §11: BONK CAM replays the last moments before the local KO; the room keeps running underneath. */
  function startBonkCam(): void {
    bonkCamAt = -1;
    const slice = game.replay.bonkCam;
    if (source === 'online' && slice && !gallery) replays.play(slice, 'bonkcam', { under: game });
  }

  /** §9.5 INTERMISSION: the round's highlight with the VHS wipe (ClientGame prepares it at round:end). */
  function startInstantReplay(): void {
    skipVoted = false;
    if (gallery) {
      gallery.playReplay('instant');
      return;
    }
    const slice = game.instantReplay;
    if (source === 'online' && slice && !everyoneSkipped(room.dto)) replays.play(slice, 'instant', { under: game });
  }

  /** §9.7: Play of the Match right before the Awards (ClientGame prepares it at match:end). */
  function startPlayOfMatch(): void {
    skipVoted = false;
    if (gallery) {
      gallery.playReplay('potm');
      return;
    }
    const slice = game.playOfMatch;
    if (source === 'online' && slice) replays.play(slice, 'potm', { under: game });
  }

  /**
   * The shared replay skip is collective (§9.5): it ends once every connected human pressed Skip
   * (`room.skip`, WP3). Solo, the local press is the whole vote.
   */
  function connectedHumans(): number {
    return room.dto ? room.dto.players.filter((p) => p !== null && !p.isBot && p.connected).length : 0;
  }

  function everyoneSkipped(dto: RoomStateDTO | null): boolean {
    if (!dto) return false;
    const skip = dto.skip ?? [];
    let humans = 0;
    for (const p of dto.players) {
      if (!p || p.isBot || !p.connected) continue;
      humans++;
      if (!skip.includes(p.slot)) return false;
    }
    return humans > 0;
  }

  function enterMenu(): void {
    source = 'local';
    practiceSetup = null;
    ghostSpectating = false;
    if (!local.active || local.config !== MENU_SESSION) local.start(MENU_SESSION);
    audio.setMenu(true);
    shell.menu();
  }

  function enterPractice(setup: PracticeSetup): void {
    source = 'local';
    practiceSetup = setup;
    local.start(practiceConfig(setup));
    audio.setMenu(false);
    shell.practice();
  }

  function goOnline(msg: ClientMsg | null): void {
    pendingJoin = true;
    transport.connect();
    if (msg) transport.send(msg);
  }

  function enterRoom(): void {
    pendingJoin = false;
    source = 'online';
    practiceSetup = null;
    ghostSpectating = false;
    game.spectateAsGhost(false);
    local.stop();
    audio.setMenu(false);
    shell.room();
  }

  function leaveToMenu(): void {
    stopReplay();
    if (gallery) gallery.server.send({ t: 'leaveRoom' });
    else {
      if (source === 'online' || pendingJoin) game.leave();
      if (net.status !== 'online') net.close();
    }
    pendingJoin = false;
    room.reset();
    enterMenu();
  }

  function proceed(): void {
    proceeded = true;
    if (gallery) {
      enterMenu();
      gallery.start({ shell, room, goOnline, leave: leaveToMenu });
      return;
    }
    if (params.practice) {
      enterPractice({ mapId: params.map ?? MapId.TEACUP, modeId: params.mode ?? ModeId.ROYALE, bots: params.bots ?? 3, difficulty: params.difficulty ?? Difficulty.NORMAL, dummies: true });
      return;
    }
    enterMenu();
    const req = roomRequestFromParams(params);
    if (params.pathRoom) goOnline({ t: 'joinRoom', code: params.pathRoom });
    else if (params.autostart) goOnline({ t: 'createRoom', settings: req.settings, debug: req.debug });
    else if (params.quick) goOnline({ t: 'quickPlay' });
    else if (params.room) goOnline({ t: 'joinRoom', code: params.room });
    else if (net.roomSession) goOnline(null);
  }

  function needsName(): boolean {
    return profiles.get().name === '';
  }

  function spectating(): boolean {
    return source === 'online' && (room.spectator || ghostSpectating);
  }

  const services: UiServices = {
    portrait: (c, identity, size) => portraits.get(c, identity, size),
    worldToScreen: (x, y, z, out) => renderer.worldToScreen(x, y, z, out),
    locker: {
      enter() {
        lockerFrames = 0;
        lockerOpen = true;
        renderer.setStage('locker');
        const p = profiles.get();
        renderer.locker.setCosmetics(p.cosmetics, p.cosmetics.identityPref < 8 ? p.cosmetics.identityPref : 0);
      },
      exit: () => {
        lockerOpen = false;
        renderer.setStage('game');
      },
      set: (c, identity) => renderer.locker.setCosmetics(c, identity),
      spin: (dx) => renderer.locker.spin(dx),
    },
    playUi: (id) => audio.playUi(id),
    preview: (kind, id) => audio.preview(kind, id),
    captions: () => gallery?.captions() ?? audio.captions,
    input,
    profiles,
    settings: settingsStore,
    prompts,
    tapestry: {
      rounds: () => tapestry.recorded,
      render: (roundId, info) => {
        try {
          return tapestry.render(roundId, info);
        } catch {
          return null;
        }
      },
    },
    replay: () => presented.replay,
    // World-space UI (nameplates, mash prompt, offscreen arrows) sits on the beans as drawn.
    presented: () => drawn,
    setHudInsets: (h) => renderer.setHudInsets(h),
    setNameplateRects: (rects) => renderer.setNameplateRects(rects),
    spectating,
    quality: () => renderer.stats.tier,
    loading: () => ({ fonts: fontsLoaded, world: renderer.mapReady && renderer.framesSinceRound >= 4, audio: audio.unlocked }),
    practiceInfo: () => ({ active: source === 'local' && practiceSetup !== null, freePlay: practiceSetup !== null && local.freePlay, setup: practiceSetup }),
  };

  const shell = createShell(uiRoot, {
    bus, room, services,
    profile: () => profiles.get(),
    settings: () => settings,
    view: () => lastView,
    actions: {
      start() {
        unlockAudio();
        if (needsName()) shell.namePrompt();
        else proceed();
      },
      setName(name) {
        profiles.setName(name);
        if (!proceeded) proceed();
        else shell.menu();
      },
      quickPlay: () => {
        // §14.6: fullscreen on the first QUICK PLAY tap on Android (iOS cannot).
        if (input.device === 'touch' && /Android/i.test(navigator.userAgent) && !document.fullscreenElement) {
          void document.documentElement.requestFullscreen?.().catch(() => undefined);
        }
        goOnline({ t: 'quickPlay' });
      },
      createRoom: (s: Partial<RoomSettings>) => goOnline({ t: 'createRoom', settings: s }),
      joinRoom: (code) => goOnline({ t: 'joinRoom', code }),
      practice: enterPractice,
      restartPractice: () => {
        if (practiceSetup) local.restart();
      },
      practiceItem: (kind) => {
        if (practiceSetup) local.giveItem(kind);
      },
      leave: leaveToMenu,
      send: (msg) => transport.send(msg),
      reload: () => location.reload(),
      spectate(on) {
        if (source !== 'online') return;
        // ClientGame (WP3) releases ghost control while spectating: no inputs are sent and the
        // camera never falls back to the local ghost (§9.6). It only accepts while the bean is out.
        game.spectateAsGhost(on);
        ghostSpectating = game.ghostSpectating;
        if (ghostSpectating) {
          const killer = lastKiller.get(game.localSlot) ?? NO_SLOT;
          if (killer !== NO_SLOT) game.setFocus(killer);
          else game.cycleFocus(1);
        } else {
          game.setFocus(NO_SLOT);
        }
        syncGameplay();
      },
      cycleFocus(dir) {
        if (gallery) gallery.cycleFocus(dir);
        else game.cycleFocus(dir);
      },
      skipReplay() {
        const r = replays.current ?? null;
        const kind = r?.kind ?? gallery?.replayKind() ?? null;
        if (kind === null) return;
        if (kind === 'bonkcam') {
          replays.skip();
          return;
        }
        if (kind === 'potm') {
          // Play of the Match skips locally, straight to the award cards: the server's AWARDS skip
          // ends the whole phase, so the Awards screen sends it once the cards are out.
          replays.skip();
          gallery?.stopReplay();
          return;
        }
        if (!skipVoted) {
          skipVoted = true;
          transport.send({ t: 'skip' });
        }
        // Alone, the vote is unanimous at once (§9.5); otherwise it ends when `room.skip` covers
        // every connected human (the 'room' handler above).
        if (connectedHumans() <= 1) {
          replays.skip();
          gallery?.stopReplay();
        }
      },
    },
  });


  /** Gameplay presses only while no modal screen is up and the player is not just watching. */
  function syncGameplay(): void {
    input.setGameplayEnabled(!GAMEPLAY_OFF_SCREENS.has(shell.screen) && !spectating());
  }

  // ---- Bus wiring -----------------------------------------------------------------------------
  bus.on('joined', enterRoom);
  bus.on('round:init', (init) => {
    stopReplay();
    ghostSpectating = false;
    lastKiller.clear();
    syncGameplay();
    const { mapId, modeId, seed, golden } = init.cfg;
    renderer.setRound({ mapId, modeId, seed, golden });
    audio.setRound({ mapId, modeId, golden, roundIndex: init.roundIndex, startRoomTick: init.startRoomTick });
  });
  bus.on('room', (dto) => {
    const phase = dto.phase;
    if (phase !== lastPhase) {
      // A BONK CAM never runs into the next phase (the tally, the awards).
      if (replays.current?.kind === 'bonkcam') replays.skip();
      bonkCamAt = -1;
      // The round result (`roundEnd`) and the match result (`matchEnd`) follow their phase's room
      // update: the shared replays start when they arrive (see 'round:end' / 'match:end' below).
      if (phase !== RoomPhase.INTERMISSION && phase !== RoomPhase.AWARDS) {
        if (replays.current) replays.skip();
        gallery?.stopReplay();
      }
      lastPhase = phase;
    } else if ((phase === RoomPhase.INTERMISSION || phase === RoomPhase.AWARDS) && dto.phaseEndTick < lastPhaseEnd) {
      // The server shortened the phase (everyone voted and skipped): the shared replay ends here too.
      if (replays.current && replays.current.kind !== 'bonkcam') replays.skip();
      gallery?.stopReplay();
    }
    // Collective skip (§9.5): every connected human pressed Skip, so the Instant Replay ends for all.
    if (phase === RoomPhase.INTERMISSION && everyoneSkipped(dto) && replays.current?.kind === 'instant') replays.skip();
    lastPhaseEnd = dto.phaseEndTick;
  });
  // The shared replays start as soon as their result arrives (it follows its phase's room update in
  // the same server tick), so the Tally and the Awards never begin their reveals under them.
  bus.on('round:end', () => {
    // §9.5: INTERMISSION opens with the Instant Replay (the final-KO slow motion played in ROUND_END).
    if (lastPhase === RoomPhase.INTERMISSION) startInstantReplay();
  });
  bus.on('match:end', () => {
    // §9.7: Play of the Match opens the AWARDS phase.
    if (lastPhase === RoomPhase.AWARDS) startPlayOfMatch();
  });
  onSim(bus, (ev, _local, replay) => {
    if (replay) return;
    if (ev.type === EV.ROUND_OVER) {
      // The round is decided: its final-KO slow motion and GAME! play live, never under a BONK CAM
      // of an earlier KO (WP7 only skips BONK CAM when the local KO itself was the final one).
      bonkCamAt = -1;
      if (replays.current?.kind === 'bonkcam') replays.skip();
      return;
    }
    if (ev.type !== EV.KO) return;
    lastKiller.set(ev.a, ev.b);
    // BONK CAM is the death replay before ghost mode (§11): scoring rounds only, never the social
    // Lobby or the Podium Brawl, where beans just respawn.
    if (source === 'online' && ev.a === game.localSlot && !gallery && room.dto?.phase === RoomPhase.PLAY) bonkCamAt = now() + BONK_CAM_DELAY_MS;
  });
  bus.on('input:nav', ({ action }) => {
    if (action === 'confirm' && replays.current?.kind === 'bonkcam') replays.skip();
  });
  bus.on('net:status', ({ state, attempt }) => {
    reconnectAttempt = state === 'reconnecting' ? attempt : 0;
  });
  bus.on('error', ({ code, msg }) => {
    if (code === 'ROOM_NOT_FOUND' && createIfMissing && params.room) {
      createIfMissing = false;
      const req = roomRequestFromParams(params);
      transport.send({ t: 'createRoom', settings: req.settings, debug: req.debug });
      return;
    }
    if (code === 'KICKED') {
      leaveToMenu();
      bus.emit('toast', { text: KICK_TOASTS[msg] ?? KICK_TOASTS.abuse, kind: 'leave' });
      return;
    }
    if (code === 'VERSION_MISMATCH') {
      net.close();
      shell.error({ message: 'A new version is available — reload', reload: true, title: 'Fresh beans available!' });
      return;
    }
    const joinError = JOIN_ERROR_TEXT[code];
    if (joinError === undefined) {
      bus.emit('toast', { text: ERROR_TOASTS[code] ?? msg, kind: 'error' });
      return;
    }
    // A join or rejoin failed.
    pendingJoin = false;
    if (source === 'online') {
      leaveToMenu();
      bus.emit('toast', { text: 'Could not rejoin the room', kind: 'error' });
    } else if (shell.screen !== 'joinCode') {
      shell.error({ message: joinError, reload: false, code });
    }
  });
  bus.on('ui:screen', syncGameplay);
  bus.on('settings', (s) => {
    settings = s;
    applyVolumes();
  });
  // Cosmetics changed in the Locker while in a room: the server applies them (§9.3).
  let sentCosmetics = profiles.get().cosmetics;
  bus.on('profile', (p) => {
    if (source !== 'online' || sameCosmetics(p.cosmetics, sentCosmetics)) return;
    sentCosmetics = p.cosmetics;
    transport.send({ t: 'cosmetics', cosmetics: p.cosmetics });
  });
  bus.on('joined', () => {
    sentCosmetics = profiles.get().cosmetics;
  });
  bus.on('quality', (tier) => {
    if (settings.graphics.quality === 'auto') bus.emit('toast', { text: `Graphics set to ${tier[0].toUpperCase()}${tier.slice(1)} for smoothness`, kind: 'info' });
  });
  // A pending reconnect (§9.6, §20.11) retries at once when the browser is back online or the tab is shown
  // again, instead of waiting out the rest of its backoff.
  window.addEventListener('online', () => net.retryNow());
  // The browser knows the network is gone: drop the socket now instead of waiting out Net's
  // silence watchdog (3.5 s), so "Reconnecting…" shows at once.
  window.addEventListener('offline', () => net.networkLost());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      game.hardResync();
      net.retryNow();
    }
    // §14.4 "Mute when tab hidden".
    if (settings.audio.muteWhenHidden) audio.setVolumes({ ...settings.audio, master: document.hidden || muted ? 0 : settings.audio.master });
  });
  // Audio unlocks on the first gesture even when the boot screen is skipped (§9.1).
  for (const type of ['pointerdown', 'keydown'] as const) window.addEventListener(type, unlockAudio, { once: true });

  // A `?debug=cosmetics|vfx|audio` page replaces the menu flow (§28).
  const pageId = gallery ? null : verificationPageId(params.debug);
  const page = pageId === null ? null : await mountVerificationPage(pageId, uiRoot, bus, audio, (r) => {
    renderer.setRound(r);
    audio.setRound({ ...r, roundIndex: 0, startRoomTick: 0 });
  });

  const debug = installDebug(bus, renderer, {
    screen: () => shell.screen,
    room: () => (room.dto ? { code: room.code, phase: ROOM_PHASE_NAMES[room.dto.phase], roundIndex: room.dto.roundIndex, players: room.humans() } : null),
    view: () => presented,
    tick: () => (source === 'online' && !gallery ? game.tickDebug() : { room: local.tick, round: lastView.roundTick, render: lastView.renderTick, local: lastView.localTick }),
    net: () => game.netDebug(),
    hash: () => {
      if (source === 'online' && !gallery) return game.authHash();
      const w = local.currentWorld;
      return w ? hashWorld(w) : 0;
    },
    screenshotReady: () => fontsLoaded && (page === null || page.ready()) && (gallery === null || gallery.ready())
      && (page?.hidesGame === true || (lockerOpen ? lockerFrames >= SCREENSHOT_MIN_FRAMES : renderer.mapReady && renderer.framesSinceRound >= SCREENSHOT_MIN_FRAMES)),
    send: (msg) => transport.send(msg),
  }, uiRoot, () => params.debug === 'stats' || settings.graphics.showStats);

  function connectionState(): ConnectionState {
    if (gallery) return gallery.connection();
    if (source !== 'online') {
      if (!pendingJoin) return 'hidden';
      if (net.status === 'reconnecting') return reconnectAttempt >= CONNECT_GIVE_UP ? 'disconnected' : 'reconnecting';
      return net.status === 'online' ? 'joining' : 'connecting';
    }
    if (net.status === 'offline') return 'disconnected';
    if (net.status === 'reconnecting') return reconnectAttempt >= RECONNECT_GIVE_UP ? 'disconnected' : 'reconnecting';
    if (net.status === 'connecting') return 'connecting';
    const phase = room.phase;
    return phase === null || (STEPPING_PHASES.includes(phase) && !game.ready) ? 'joining' : 'hidden';
  }

  // 6. Initial screen (the diorama runs behind the boot screen and name prompt).
  if (page === null) {
    local.start(MENU_SESSION);
    audio.setMenu(true);
    if (!params.skipIntro) shell.boot();
    else if (needsName()) shell.namePrompt();
    else proceed();
  }

  // 8. The frame loop (§23.2).
  const frame = (t: number): void => {
    if (!gallery) net.pump();
    input.poll();
    if (bonkCamAt >= 0 && t >= bonkCamAt) startBonkCam();
    const live: FrameSource = page?.source ?? (source === 'online' ? liveSource() : local);
    const view = (replays.current ?? live).frame(t);
    // Input, the HUD and the Tapestry follow the round itself, also while a replay is presented.
    const liveView = view.source === 'replay' ? game.view : view;
    if (source === 'local' || gallery) syncAimContext(aim, liveView);
    input.syncView(liveView);
    if (source === 'online') tapestry.sample(liveView);
    excitement.update(view);
    if (!page?.hidesGame) renderer.render(view);
    if (lockerOpen) lockerFrames++;
    audio.update(view);
    presented = view;
    drawn = page?.hidesGame || lockerOpen ? view : renderer.presented;
    lastView = liveView;
    shell.update(liveView);
    shell.connection(connectionState(), gallery ? GALLERY_RECONNECT_ATTEMPT : reconnectAttempt);
    debug.update(view);
    page?.update(view);
    gallery?.update(view);
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
  debug.markReady();
}

void boot();
